import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const API = axios.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });

const STATUS_COLORS = {
  pending: { bg: '#fff3e0', color: '#e67e00' },
  approved: { bg: '#e8f5e9', color: '#2e7d32' },
  rejected: { bg: '#fce4ec', color: '#c62828' },
  on_hold: { bg: '#e3f2fd', color: '#1565c0' },
  dispatched: { bg: '#f3e8ff', color: '#7B2D6B' },
};

const normalizeDate = (d) => {
  if (!d) return '';
  // Already in yyyy-MM-dd format
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  // Try to parse various formats
  try {
    const parts = d.replace(/[./]/g, '/').split('/');
    if (parts.length === 3) {
      let [a, b, c2] = parts.map(p => p.trim());
      // dd/mm/yy or dd/mm/yyyy
      if (c2.length === 2) c2 = '20' + c2;
      if (a.length <= 2 && b.length <= 2 && c2.length === 4) {
        return c2 + '-' + b.padStart(2,'0') + '-' + a.padStart(2,'0');
      }
    }
  } catch(e) {}
  return '';
};

const EMPTY_FORM = {
  sample_no: '', rd_code: '', sample_name: '', requester: '', preparation: 'RND',
  department: '', request_date: '', preparation_date: '', rd_approval: 'no',
  quality_submitted: 'no', quality_approval_date: '', handover_date: '',
  pack_size: '', notes: '', status: 'pending', product_id: '', product_code: '', linked_product_code: ''
};

function SampleProductSearch({ products, value, onSelect }) {
  const sel = products.find(p => String(p.id) === String(value));
  const [q, setQ] = React.useState(sel ? sel.name + ' (' + (sel.product_code||'') + ')' : '');
  const [res, setRes] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    const s = products.find(p => String(p.id) === String(value));
  }, [value, products]);
  const search = (v) => {
    setQ(v);
    const words = v.toLowerCase().split(/\s+/).filter(Boolean);
    const r = products.filter(p => { const h=((p.name||'')+(p.product_code||'')).toLowerCase(); return words.length===0||words.every(w=>h.includes(w)); }).slice(0,20);
    setRes(r); setOpen(r.length>0);
  };
  return (
    <div style={{position:'relative'}}>
      <input type="text" value={q} onChange={e=>search(e.target.value)} placeholder="Search product..." onBlur={()=>setTimeout(()=>setOpen(false),200)} style={{width:'100%'}} />
      {open&&(<div style={{position:'absolute',top:'100%',left:0,right:0,background:'white',border:'1px solid var(--border)',borderRadius:8,maxHeight:200,overflowY:'auto',zIndex:500,boxShadow:'0 4px 12px rgba(0,0,0,0.12)'}}>
        <div onMouseDown={()=>{onSelect('');setQ('');setOpen(false);}} style={{padding:'8px 12px',cursor:'pointer',fontSize:12,color:'var(--text-muted)',borderBottom:'1px solid var(--border)'}}>— Not linked —</div>
        {res.map(p=>(<div key={p.id} onMouseDown={()=>{onSelect(p.id);setQ(p.name+' ('+(p.product_code||'')+')');setOpen(false);}} style={{padding:'8px 12px',cursor:'pointer',fontSize:12,borderBottom:'1px solid var(--border)'}} onMouseEnter={e=>e.currentTarget.style.background='var(--bg-hover)'} onMouseLeave={e=>e.currentTarget.style.background='white'}><span style={{fontWeight:600}}>{p.name}</span><span style={{fontFamily:'monospace',color:'var(--accent)',marginLeft:8,fontSize:11}}>{p.product_code}</span></div>))}
      </div>)}
    </div>
  );
}

export default function SampleTracking() {
  const [samples, setSamples] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editSample, setEditSample] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    loadSamples();
    API.get('/products').then(r => setProducts(r.data)).catch(() => {});
  }, []);

  const loadSamples = () => {
    setLoading(true);
    API.get('/samples').then(r => { setSamples(r.data); setLoading(false); }).catch(() => setLoading(false));
  };

  const depts = [...new Set(samples.map(s => s.department).filter(Boolean))];

  const filtered = samples.filter(s => {
    const words = search.toLowerCase().split(/\s+/).filter(Boolean);
    const hay = [s.sample_name, s.rd_code, s.requester, s.department, s.notes].join(' ').toLowerCase();
    const matchSearch = words.length === 0 || words.every(w => hay.includes(w));
    const matchStatus = !filterStatus || s.status === filterStatus;
    const matchDept = !filterDept || s.department === filterDept;
    return matchSearch && matchStatus && matchDept;
  });

  const handleSave = async () => {
    if (!form.sample_name && !form.rd_code) return toast.error('Sample name or R&D code required');
    setSaving(true);
    try {
      if (editSample) await API.put('/samples/' + editSample.id, form);
      else await API.post('/samples', form);
      toast.success(editSample ? 'Sample updated!' : 'Sample added!');
      setShowForm(false); setEditSample(null); setForm(EMPTY_FORM);
      loadSamples();
    } catch(e) { toast.error('Failed to save'); }
    setSaving(false);
  };

  const handleEdit = (s) => {
    setEditSample(s);
    setForm({ ...EMPTY_FORM, ...s, product_id: s.product_id || '', product_code: s.product_code || '', request_date: normalizeDate(s.request_date), preparation_date: normalizeDate(s.preparation_date), quality_approval_date: normalizeDate(s.quality_approval_date), handover_date: normalizeDate(s.handover_date) });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this sample?')) return;
    await API.delete('/samples/' + id);
    loadSamples();
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    const text = await file.text();
    const lines = text.split('\n').slice(1);
    const samples = [];
    for (const line of lines) {
      const row = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
      if (!row[1] && !row[11]) continue;
      if (row[0] && !row[0].match(/^\d+$/)) continue;
      samples.push({
        sample_no: row[0]||'', sample_name: row[1]||'', requester: row[2]||'',
        preparation: row[3]||'', department: row[4]||'', request_date: row[5]||'',
        preparation_date: row[6]||'', rd_approval: (row[7]||'').toLowerCase().includes('yes') ? 'yes' : 'no',
        quality_submitted: (row[8]||'').toLowerCase().includes('yes') ? 'yes' : 'no',
        quality_approval_date: row[9]||'', handover_date: row[10]||'',
        rd_code: row[11]||'', notes: row[12]||''
      });
    }
    try {
      const r = await API.post('/samples/import', { samples });
      toast.success(r.data.inserted + ' samples imported!');
      loadSamples();
    } catch(e) { toast.error('Import failed'); }
    setImporting(false);
  };

  const stats = {
    total: samples.length,
    pending: samples.filter(s => s.status === 'pending').length,
    approved: samples.filter(s => s.status === 'approved').length,
    dispatched: samples.filter(s => s.status === 'dispatched').length,
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Sample Tracking</div>
          <div className="page-subtitle">Track samples given to sales and customers</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => fileRef.current.click()} disabled={importing}>
            {importing ? 'Importing...' : '📥 Import CSV'}
          </button>
          <input ref={fileRef} type="file" accept=".csv" onChange={handleImport} style={{ display: 'none' }} />
          <button className="btn btn-primary" onClick={() => { setShowForm(true); setEditSample(null); setForm(EMPTY_FORM); }}>
            + New Sample
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
        {[['Total', stats.total, '#7B2D6B', '#fff'],['Pending', stats.pending, '#fff3e0', '#e67e00'],['Approved', stats.approved, '#e8f5e9', '#2e7d32'],['Dispatched', stats.dispatched, '#f3e8ff', '#7B2D6B']].map(([l,v,bg,col]) => (
          <div key={l} className="stat-card" style={{ background: bg }}>
            <div className="stat-value" style={{ color: col }}>{v}</div>
            <div className="stat-label">{l}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 16, padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <input type="text" placeholder="🔍 Search by name, R&D code, requester..."
            value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 2, minWidth: 200 }} />
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ flex: 1, minWidth: 130 }}>
            <option value="">All Statuses</option>
            {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s.replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase())}</option>)}
          </select>
          <select value={filterDept} onChange={e => setFilterDept(e.target.value)} style={{ flex: 1, minWidth: 130 }}>
            <option value="">All Departments</option>
            {depts.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          {(search || filterStatus || filterDept) && <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setFilterStatus(''); setFilterDept(''); }}>✕ Clear</button>}
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto' }}>Showing {filtered.length} of {samples.length}</span>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div> :
        filtered.length === 0 ? (
          <div className="empty-state"><div className="empty-state-icon">🧪</div><p>No samples found.</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 50 }}>No.</th>
                  <th>R&D Code</th>
                  <th>Product Code</th>
                  <th>Sample Name</th>
                  <th>Requester</th>
                  <th>Dept</th>
                  <th>Prep By</th>
                  <th>Request Date</th>
                  <th>R&D ✓</th>
                  <th>QA ✓</th>
                  <th>Handover</th>
                  <th>Pack Size</th>
                  <th>Status</th>
                  <th>Product</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(s => {
                  const sc = STATUS_COLORS[s.status] || { bg: '#f5f5f5', color: '#666' };
                  return (
                    <tr key={s.id}>
                      <td style={{ textAlign: 'center', fontWeight: 600 }}>{s.sample_no}</td>
                      <td><span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--accent)' }}>{s.rd_code || '—'}</span></td>
                      <td><span style={{ fontFamily: 'monospace', fontSize: 11 }}>{s.product_code || '—'}</span></td>
                      <td style={{ fontWeight: 500, maxWidth: 200 }}>{s.sample_name}</td>
                      <td style={{ fontSize: 12 }}>{s.requester || '—'}</td>
                      <td style={{ fontSize: 12 }}>{s.department || '—'}</td>
                      <td style={{ fontSize: 12 }}>{s.preparation || '—'}</td>
                      <td style={{ fontSize: 12 }}>{s.request_date || '—'}</td>
                      <td style={{ textAlign: 'center' }}>{s.rd_approval === 'yes' ? '✅' : '—'}</td>
                      <td style={{ textAlign: 'center' }}>{s.quality_submitted === 'yes' ? '✅' : '—'}</td>
                      <td style={{ fontSize: 12 }}>{s.handover_date || '—'}</td>
                      <td style={{ fontSize: 12 }}>{s.pack_size || '—'}</td>
                      <td><span style={{ background: sc.bg, color: sc.color, padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>{s.status?.replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase())}</span></td>
                      <td style={{ fontSize: 12 }}>{s.product_name || '—'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => handleEdit(s)}>Edit</button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(s.id)}>✕</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal modal-lg">
            <div className="modal-header">
              <div className="modal-title">{editSample ? 'Edit Sample' : 'New Sample'}</div>
              <button className="modal-close" onClick={() => { setShowForm(false); setEditSample(null); }}>✕</button>
            </div>
            <div className="form-grid">
              <div className="form-group"><label>Sample No</label><input value={form.sample_no} onChange={e => setForm({...form, sample_no: e.target.value})} placeholder="Auto-assigned if blank" /></div>
              <div className="form-group"><label>R&D Code</label><input value={form.rd_code} onChange={e => setForm({...form, rd_code: e.target.value})} placeholder="e.g. KRD112501" style={{ fontFamily: 'monospace' }} /></div>
              <div className="form-group"><label>Product Code (links to product)</label><input value={form.product_code} onChange={e => setForm({...form, product_code: e.target.value})} placeholder="e.g. CGPR-001" style={{ fontFamily: 'monospace' }} /></div>
              <div className="form-group full"><label>Sample Name *</label><input value={form.sample_name} onChange={e => setForm({...form, sample_name: e.target.value})} placeholder="e.g. Peanut Blanched Light Salted" /></div>
              <div className="form-group"><label>Requester</label><input value={form.requester} onChange={e => setForm({...form, requester: e.target.value})} /></div>
              <div className="form-group"><label>Department Requested</label><input value={form.department} onChange={e => setForm({...form, department: e.target.value})} /></div>
              <div className="form-group"><label>Preparation By</label>
                <select value={form.preparation} onChange={e => setForm({...form, preparation: e.target.value})}>
                  <option value="RND">R&D</option>
                  <option value="Production">Production</option>
                  <option value="Lab Trials">Lab Trials</option>
                  <option value="External">External</option>
                </select>
              </div>
              <div className="form-group"><label>Pack Size</label><input value={form.pack_size} onChange={e => setForm({...form, pack_size: e.target.value})} placeholder="e.g. 500g, 1kg" /></div>
              <div className="form-group"><label>Request Date</label><input type="date" value={form.request_date} onChange={e => setForm({...form, request_date: e.target.value})} /></div>
              <div className="form-group"><label>Preparation Date</label><input type="date" value={form.preparation_date} onChange={e => setForm({...form, preparation_date: e.target.value})} /></div>
                <SampleProductSearch products={products} value={form.product_id} onSelect={pid => setForm({...form, product_id: pid})} />
              <div className="form-group"><label>Submitted for QA</label>
                <select value={form.quality_submitted} onChange={e => setForm({...form, quality_submitted: e.target.value})}>
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>
              <div className="form-group"><label>QA Approval Date</label><input type="date" value={form.quality_approval_date} onChange={e => setForm({...form, quality_approval_date: e.target.value})} /></div>
              <div className="form-group"><label>Sample Handover Date</label><input type="date" value={form.handover_date} onChange={e => setForm({...form, handover_date: e.target.value})} /></div>
              <div className="form-group"><label>Status</label>
                <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="dispatched">Dispatched</option>
                  <option value="rejected">Rejected</option>
                  <option value="on_hold">On Hold</option>
                </select>
              </div>
              <div className="form-group"><label>Link to Product</label>
                <SampleProductSearch products={products} value={form.product_id} onSelect={pid => setForm({...form, product_id: pid})} />
              </div>
              <div className="form-group full"><label>Notes</label><textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setShowForm(false); setEditSample(null); }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : editSample ? 'Update' : 'Add Sample'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
